import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { integrationService } from "../../services/integrationService";
import { defectService } from "../../services/defectService";
import { testcaseService } from "../../services/testcaseService";
import toast from "react-hot-toast";
import { InlineLoadSkeleton } from "../common/InlineLoadSkeleton";
import { Skeleton } from "../common/Skeleton";

const PRIORITIES = ["Highest", "High", "Medium", "Low", "Lowest"];

const LINK_TYPES = [
  { value: "Relates", label: "Relates to" },
  { value: "Blocks", label: "Blocks" },
  { value: "is blocked by", label: "Is blocked by" },
  { value: "Duplicate", label: "Duplicate" },
  { value: "Cloners", label: "Cloners" },
];

function normalizeJiraLabelList(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object" && item.name != null)
        return String(item.name).trim();
      if (item && typeof item === "object" && item.label != null)
        return String(item.label).trim();
      return "";
    })
    .filter(Boolean);
}

export default function PushNewDefectModal({
  open,
  onClose,
  clientId,
  projectId,
  onSuccess,
}) {
  const [tool, setTool] = useState("Jira");
  const [jiraProject, setJiraProject] = useState("");
  const [title, setTitle] = useState("");
  const [testCaseId, setTestCaseId] = useState("");
  const [priority, setPriority] = useState("High");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [labels, setLabels] = useState([]);
  const [labelInput, setLabelInput] = useState("");
  const [jiraProjects, setJiraProjects] = useState([]);
  const [jiraAssignees, setJiraAssignees] = useState([]);
  const [jiraLabels, setJiraLabels] = useState([]);
  const [jiraBoards, setJiraBoards] = useState([]);
  const [jiraSprints, setJiraSprints] = useState([]);
  const [jiraBoardId, setJiraBoardId] = useState("");
  const [sprintId, setSprintId] = useState("");
  const [linkToIssueKey, setLinkToIssueKey] = useState("");
  const [linkType, setLinkType] = useState("Relates");
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    if (!open || !clientId || !projectId) return;
    setLoadingMeta(true);
    Promise.all([
      integrationService
        .getJiraProjects(clientId)
        .catch(() => ({ projects: [] })),
      testcaseService
        .getTestCases(projectId, {})
        .catch(() => ({ test_cases: [] })),
    ])
      .then(([jiraRes, tcRes]) => {
        const projects = jiraRes?.projects ?? jiraRes?.data?.projects ?? [];
        setJiraProjects(Array.isArray(projects) ? projects : []);
        const list =
          tcRes?.test_cases ??
          tcRes?.data?.test_cases ??
          (Array.isArray(tcRes) ? tcRes : []);
        setTestCases(Array.isArray(list) ? list : []);
      })
      .finally(() => setLoadingMeta(false));
  }, [open, clientId, projectId]);

  // Jira labels are global — load as soon as modal opens (do not wait for project pick).
  useEffect(() => {
    if (!open || !clientId || tool !== "Jira") {
      setJiraLabels([]);
      return;
    }
    setLoadingLabels(true);
    integrationService
      .getJiraLabels(clientId)
      .then((res) => {
        const raw = res?.labels ?? res?.data?.labels ?? [];
        setJiraLabels(normalizeJiraLabelList(raw));
      })
      .catch(() => setJiraLabels([]))
      .finally(() => setLoadingLabels(false));
  }, [open, clientId, tool]);

  // When Jira project is selected, fetch assignable users for assignee dropdown
  useEffect(() => {
    if (!open || !clientId || tool !== "Jira" || !jiraProject) {
      setJiraAssignees([]);
      return;
    }
    const projectKey =
      typeof jiraProject === "object" ? jiraProject?.key : String(jiraProject);
    setAssignee("");
    setLoadingAssignees(true);
    integrationService
      .getJiraAssignableUsers(clientId, projectKey)
      .then((res) => {
        const list = res?.users ?? res?.data?.users ?? [];
        setJiraAssignees(Array.isArray(list) ? list : []);
      })
      .catch(() => setJiraAssignees([]))
      .finally(() => setLoadingAssignees(false));
  }, [open, clientId, tool, jiraProject]);

  // When Jira project selected: fetch boards (for sprints). Labels load separately on modal open.
  useEffect(() => {
    if (!open || !clientId || tool !== "Jira" || !jiraProject) {
      setJiraBoards([]);
      setJiraSprints([]);
      setJiraBoardId("");
      setSprintId("");
      return;
    }
    const projectKey =
      typeof jiraProject === "object" ? jiraProject?.key : String(jiraProject);
    integrationService
      .getJiraBoardsForProject(clientId, projectKey)
      .then((res) => {
        const boards = res?.boards ?? res?.data?.boards ?? [];
        const arr = Array.isArray(boards) ? boards : [];
        setJiraBoards(arr);
        setJiraBoardId(arr.length > 0 ? String(arr[0].id) : "");
      })
      .catch(() => {
        setJiraBoards([]);
        setJiraBoardId("");
      });
  }, [open, clientId, tool, jiraProject]);

  // When board selected, fetch sprints
  useEffect(() => {
    if (!open || !clientId || tool !== "Jira" || !jiraBoardId) {
      setJiraSprints([]);
      setSprintId("");
      return;
    }
    setLoadingSprints(true);
    integrationService
      .getJiraSprints(clientId, jiraBoardId)
      .then((res) => {
        const list = res?.sprints ?? res?.data?.sprints ?? [];
        setJiraSprints(Array.isArray(list) ? list : []);
        setSprintId("");
      })
      .catch(() => setJiraSprints([]))
      .finally(() => setLoadingSprints(false));
  }, [open, clientId, tool, jiraBoardId]);

  const reset = () => {
    setTitle("");
    setTestCaseId("");
    setPriority("High");
    setDescription("");
    setAssignee("");
    setAttachments([]);
    setLabels([]);
    setLabelInput("");
    setJiraProject("");
    setJiraBoardId("");
    setSprintId("");
    setLinkToIssueKey("");
    setLinkType("Relates");
  };

  const addLabel = (label) => {
    const v = (typeof label === "string" ? label : labelInput)
      .trim()
      .toLowerCase();
    if (!v) return;
    if (!labels.includes(v)) setLabels((prev) => [...prev, v]);
    setLabelInput("");
  };

  const removeLabel = (idx) => {
    setLabels((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title?.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!clientId || !projectId) {
      toast.error("Project context is required");
      return;
    }
    if (tool === "Jira") {
      if (jiraProjects.length === 0) {
        toast.error(
          "Cannot push to Jira: no projects available. Configure Jira in Settings → Integrations.",
        );
        return;
      }
      if (!jiraProject || String(jiraProject).startsWith("_proj_")) {
        toast.error("Please select a Jira project");
        return;
      }
    }
    setLoading(true);
    try {
      if (tool === "Jira" && jiraProjects.length > 0) {
        const projectKey =
          typeof jiraProject === "object"
            ? jiraProject?.key
            : String(jiraProject);
        const key =
          projectKey ||
          (jiraProjects[0] && (jiraProjects[0].key || jiraProjects[0].id));
        const ticketData = {
          client_id: clientId,
          project_key: key,
          summary: title.trim(),
          description: description || "",
          issue_type: "Bug",
          priority,
          assignee:
            assignee && !String(assignee).startsWith("_")
              ? assignee
              : undefined,
          labels: labels.length ? labels : undefined,
          sprint_id:
            sprintId && !String(sprintId).startsWith("_sprint_")
              ? sprintId
              : undefined,
          link_to_issue_key: linkToIssueKey?.trim() || undefined,
          link_type: linkType || undefined,
          attachments: attachments.length ? attachments : undefined,
        };
        const jiraRes = await integrationService.createJiraTicket(ticketData);
        const issue = jiraRes?.issue || jiraRes?.data?.issue || jiraRes;
        const jiraIssueId = issue?.id != null ? String(issue.id) : null;
        const externalId =
          issue?.key || (jiraIssueId && !issue?.key ? jiraIssueId : null);
        let status = "Open";
        let external_status = null;
        if (externalId && clientId) {
          try {
            const jiraIssue = await integrationService.getJiraIssue(
              issue?.key || externalId,
              clientId,
            );
            const fields = jiraIssue?.fields || jiraIssue?.data?.fields;
            const jiraStatusName = fields?.status?.name || fields?.status;
            if (jiraStatusName) {
              external_status = jiraStatusName;
              const s = String(jiraStatusName).toLowerCase();
              if (s === "done" || s === "closed") status = "Closed";
              else if (s === "in progress" || s === "inprogress")
                status = "In Progress";
              else if (s === "reopened") status = "Reopened";
              else if (s === "resolved") status = "Resolved";
              else if (s === "to do" || s === "todo" || s === "open")
                status = "Open";
              else status = "Open";
            }
          } catch {
            // keep default Open if fetch fails
          }
        }
        const selectedAssignee = jiraAssignees.find(
          (u) => String(u.accountId || "") === String(assignee || ""),
        );
        const internalDefect = await defectService.create({
          client_id: clientId,
          project_id: projectId,
          external_id: externalId || null,
          jira_issue_id: jiraIssueId || undefined,
          tool: "Jira",
          title: title.trim(),
          description: description || null,
          priority,
          status,
          external_status: external_status || undefined,
          assignee_id: assignee || null,
          external_assignee_name: selectedAssignee?.displayName || null,
          external_assignee_email: selectedAssignee?.emailAddress || null,
          external_assignee_id: selectedAssignee?.accountId || assignee || null,
        });
        if (
          testCaseId &&
          !String(testCaseId).startsWith("_tc_") &&
          internalDefect?.data?.id
        ) {
          await defectService.link(
            internalDefect.data.id,
            "test_case",
            testCaseId,
            {
              client_id: clientId,
              project_id: projectId,
            },
          );
        }
        toast.success(
          externalId ? `Defect pushed as ${externalId}` : "Defect created",
        );
      } else {
        const internalDefect = await defectService.create({
          client_id: clientId,
          project_id: projectId,
          title: title.trim(),
          description: description || null,
          priority,
          status: "Open",
          assignee_id: assignee || null,
        });
        if (
          testCaseId &&
          !String(testCaseId).startsWith("_tc_") &&
          internalDefect?.data?.id
        ) {
          await defectService.link(
            internalDefect.data.id,
            "test_case",
            testCaseId,
            {
              client_id: clientId,
              project_id: projectId,
            },
          );
        }
        toast.success("Defect created");
      }
      onSuccess?.();
      handleClose();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to push defect";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Push New Defect</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Select your tool *</Label>
            <Select value={tool} onValueChange={setTool} disabled={loadingMeta}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Jira">Jira</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {tool === "Jira" && (
            <div className="grid gap-2">
              <Label>Jira Project *</Label>
              {loadingMeta ? (
                <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
                  <Skeleton className="h-9 w-full max-w-md" />
                </div>
              ) : jiraProjects.length > 0 ? (
                <Select
                  value={jiraProject || "_none"}
                  onValueChange={(v) => setJiraProject(v === "_none" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Jira project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Select project</SelectItem>
                    {jiraProjects.map((p, idx) => {
                      const val = p.key || p.id || `_proj_${idx}`;
                      return (
                        <SelectItem key={val} value={String(val)}>
                          {p.name || p.key || val}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground rounded-md border border-input bg-muted/30 px-3 py-2">
                  No Jira projects found. Configure Jira in Settings →
                  Integrations and ensure your account has access to at least
                  one project.
                </p>
              )}
            </div>
          )}
          <div className="grid gap-2">
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Login failure when using special characters."
            />
          </div>
          <div className="grid gap-2">
            <Label>Test Case ID</Label>
            <Select
              value={testCaseId || "_none"}
              onValueChange={(v) => setTestCaseId(v === "_none" ? "" : v)}
              disabled={loadingMeta}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select test case" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No test case</SelectItem>
                {testCases.map((tc, idx) => {
                  const val =
                    tc.test_case_id != null && tc.test_case_id !== ""
                      ? String(tc.test_case_id)
                      : `_tc_${idx}`;
                  return (
                    <SelectItem key={val} value={val}>
                      {tc.external_id ||
                        tc.title ||
                        tc.test_case_id ||
                        "(No id)"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Priority *</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.filter(Boolean).map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Description *</Label>
            <textarea
              className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Steps to reproduce..."
            />
          </div>
          <div className="grid gap-2">
            <Label>Attachments</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.gif"
                onChange={handleFileChange}
                className="text-sm"
              />
              <span className="text-xs text-muted-foreground">
                JPEG, PNG or GIF up to 25 MB
              </span>
            </div>
            {attachments.length > 0 && (
              <ul className="text-sm text-muted-foreground mt-1">
                {attachments.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {f.name}{" "}
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="text-destructive"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Assignee</Label>
            {tool === "Jira" && jiraProject ? (
              loadingAssignees ? (
                <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
                  <Skeleton className="h-9 w-full max-w-md" />
                </div>
              ) : (
                <Select
                  value={assignee || "_none"}
                  onValueChange={(v) => setAssignee(v === "_none" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No assignee</SelectItem>
                    {jiraAssignees.map((u, idx) => {
                      const val =
                        u.accountId && String(u.accountId).trim()
                          ? u.accountId
                          : `_user_${idx}`;
                      return (
                        <SelectItem key={val} value={val}>
                          {u.displayName ||
                            u.emailAddress ||
                            u.accountId ||
                            "(No name)"}
                          {u.emailAddress && u.displayName !== u.emailAddress
                            ? ` (${u.emailAddress})`
                            : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )
            ) : (
              <Input
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder="Assignee name or ID (optional)"
              />
            )}
          </div>
          {tool === "Jira" && (
            <>
              <div className="grid gap-2">
                <Label>Labels</Label>
                {loadingLabels ? (
                  <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5 items-center rounded-md border border-input bg-background px-2 py-1.5 min-h-[2.25rem]">
                      {labels.map((l, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                        >
                          {l}
                          <button
                            type="button"
                            onClick={() => removeLabel(i)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <div className="flex gap-1 flex-1 flex-wrap">
                        <select
                          className="text-sm bg-transparent border-0 outline-none min-w-[8rem]"
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v) addLabel(v);
                            e.target.value = "";
                          }}
                        >
                          <option value="">Add existing...</option>
                          {jiraLabels
                            .filter(
                              (lb) =>
                                !labels.some(
                                  (x) =>
                                    String(x).toLowerCase() ===
                                    String(lb).toLowerCase(),
                                ),
                            )
                            .map((lb) => (
                              <option key={lb} value={lb}>
                                {lb}
                              </option>
                            ))}
                        </select>
                        <input
                          type="text"
                          className="flex-1 min-w-[6rem] text-sm bg-transparent border-0 outline-none placeholder:text-muted-foreground"
                          placeholder="Type new label and press Enter"
                          value={labelInput}
                          onChange={(e) => setLabelInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addLabel(labelInput);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Select from existing or type a new label and press Enter.
                    </p>
                  </>
                )}
              </div>
              {jiraBoards.length > 0 && (
                <div className="grid gap-2">
                  <Label>Board (for Sprint)</Label>
                  <Select
                    value={jiraBoardId || "_none"}
                    onValueChange={(v) =>
                      setJiraBoardId(v === "_none" ? "" : v)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select board" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No board</SelectItem>
                      {jiraBoards.map((b, idx) => {
                        const v =
                          b.id != null && String(b.id) !== ""
                            ? String(b.id)
                            : `_board_${idx}`;
                        return (
                          <SelectItem key={v} value={v}>
                            {b.name || `Board ${v}`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {jiraBoardId && (
                <div className="grid gap-2">
                  <Label>Sprint</Label>
                  {loadingSprints ? (
                    <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
                      <Skeleton className="h-9 w-full max-w-md" />
                    </div>
                  ) : (
                    <Select
                      value={sprintId || "_none"}
                      onValueChange={(v) => setSprintId(v === "_none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sprint (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No sprint</SelectItem>
                        {jiraSprints.map((s, idx) => {
                          const v =
                            s.id != null && String(s.id) !== ""
                              ? String(s.id)
                              : `_sprint_${idx}`;
                          return (
                            <SelectItem key={v} value={v}>
                              {s.name || `Sprint ${v}`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div className="grid gap-2">
                <Label>Link to task / issue</Label>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    className="flex-1 min-w-[8rem]"
                    value={linkToIssueKey}
                    onChange={(e) => setLinkToIssueKey(e.target.value)}
                    placeholder="e.g. PROJ-123 or PROJ-1, PROJ-2 (optional)"
                  />
                  <Select value={linkType} onValueChange={setLinkType}>
                    <SelectTrigger className="w-[10rem]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_TYPES.filter(
                        (t) => t.value != null && t.value !== "",
                      ).map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  Link to one or more Jira issues. Separate multiple keys with
                  commas (spaces optional).
                </p>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <InlineLoadSkeleton className="h-4 w-4 mr-2" />
            ) : null}
            Push Defect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
